import { useEffect, useRef } from "react";

type NodeRef = React.RefObject<HTMLElement | null>;

/**
 * Calls `onOutside` when the user presses anywhere outside `refs` while
 * `active`. Unlike Mantine's useClickOutside (bubbling `mousedown`), this
 * listens to `pointerdown` in the capture phase, so it still fires over
 * surfaces that cancel pointer events or stop propagation, such as the
 * calendar grid's drag-and-drop, where `mousedown` never reaches the document.
 */
export function useOutsidePress(
  active: boolean,
  onOutside: () => void,
  refs: NodeRef[],
) {
  // Latest values without re-binding the listener on every render.
  const latest = useRef({ onOutside, refs });
  latest.current = { onOutside, refs };

  useEffect(() => {
    if (!active) return;
    const handlePress = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const inside = latest.current.refs.some(
        (ref) => !!target && !!ref.current?.contains(target),
      );
      if (!inside) latest.current.onOutside();
    };
    document.addEventListener("pointerdown", handlePress, true);
    return () => document.removeEventListener("pointerdown", handlePress, true);
  }, [active]);
}
